import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Remove from "@mui/icons-material/Remove";
import {
  buildWaveform,
  decodeAudioBuffer,
  drawWaveform,
  fetchRecordingBytes,
  WAVEFORM_LANE_COLORS,
} from "./callWaveform";

export const CATEGORY_META = {
  all: { label: "All", color: "#0F172A", background: "#E2E8F0" },
  lifecycle: { label: "Lifecycle", color: "#1D4ED8", background: "#DBEAFE" },
  speech: { label: "Speech", color: "#15803D", background: "#DCFCE7" },
  turn: { label: "Turns", color: "#C026D3", background: "#FAE8FF" },
  asr: { label: "ASR", color: "#D97706", background: "#FEF3C7" },
  silence: { label: "Silence", color: "#E11D48", background: "#FFE4E6" },
  other: { label: "Other", color: "#64748B", background: "#F1F5F9" },
};

export const EVENT_LEGEND_KEYS = [
  "lifecycle",
  "speech",
  "turn",
  "asr",
  "silence",
] as const;

type CategoryKey = keyof typeof CATEGORY_META;

export type CallEvent = {
  event_name: string;
  label: string;
  category: string;
  description?: string;
  event_time?: number | null;
  from_start?: number | null;
  from_connected?: number | null;
  rel_seconds?: number | null;
  delta_seconds?: number | null;
  preview?: string | null;
};

type TimedEvent = CallEvent & {
  sourceIndex: number;
  audioTime: number;
};

type SpeechRange = {
  start: number;
  end: number;
  preview?: string | null;
};

type WaveLane = {
  label: string;
  peaks: number[];
  color: string;
};

type Waveform = {
  dual: boolean;
  lanes: WaveLane[];
};

type HoverState = {
  time: number;
  x: number;
  event: TimedEvent | null;
};

type CallEventsPlayerProps = {
  audioUrl?: string | null;
  callUuid?: string;
  events: CallEvent[];
  durationHint?: number | null;
  activeIndex?: number | null;
  onTimeChange?: (time: number) => void;
  onMarkerClick?: (index: number, time: number) => void;
  seekRequest?: { time: number; id?: number } | null;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 16;
const ZOOM_STEP = 1.35;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const hexToRgba = (hex: string, alpha: number) => {
  const raw = hex.replace("#", "");
  const n = parseInt(raw, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const formatClock = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "00:00";
  const total = Math.max(0, Number(seconds));
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const formatClockMs = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "00:00.000";
  const totalMs = Math.max(0, Math.round(Number(seconds) * 1000));
  const m = Math.floor(totalMs / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};

export const connectedAudioTime = (event?: CallEvent | null) => {
  if (event?.from_connected == null || Number.isNaN(Number(event.from_connected))) {
    return null;
  }
  return Number(event.from_connected);
};

const pairSpeechRanges = (timedEvents: TimedEvent[]): SpeechRange[] => {
  const ranges: SpeechRange[] = [];
  let open: TimedEvent | null = null;
  timedEvents.forEach((event) => {
    if (event.event_name === "AI_STARTED_SPEAKING_SENTENCE") {
      open = event;
      return;
    }
    if (event.event_name === "AI_COMPLETED_SPEAKING_SENTENCE" && open) {
      ranges.push({
        start: open.audioTime,
        end: event.audioTime,
        preview: open.preview || event.preview,
      });
      open = null;
    }
  });
  return ranges.filter(
    (range) =>
      range.start != null && range.end != null && range.end > range.start
  );
};

const axisTicks = (duration: number, zoom = 1) => {
  if (!duration || duration <= 0) return [0];
  const visible = duration / Math.max(zoom, 1);
  const step =
    visible <= 6
      ? 1
      : visible <= 15
      ? 2
      : visible <= 40
      ? 5
      : visible <= 90
      ? 10
      : visible <= 180
      ? 15
      : 30;
  const ticks: number[] = [];
  for (let t = 0; t <= duration; t += step) ticks.push(t);
  if (ticks[ticks.length - 1] < duration - 1) ticks.push(duration);
  return ticks;
};

const CallEventsPlayer = ({
  audioUrl,
  callUuid,
  events,
  durationHint,
  activeIndex,
  onTimeChange,
  onMarkerClick,
  seekRequest,
}: CallEventsPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const zoomRef = useRef(1);
  const pendingScrollRef = useRef<{ ratio: number; viewOffset: number } | null>(
    null
  );
  const onTimeChangeRef = useRef(onTimeChange);
  onTimeChangeRef.current = onTimeChange;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [zoom, setZoom] = useState(1);
  const [waveform, setWaveform] = useState<Waveform | null>(null);
  zoomRef.current = zoom;

  const timelineDuration =
    (audioDuration && Number.isFinite(audioDuration) ? audioDuration : 0) ||
    Number(durationHint) ||
    0;

  const innerWidth = () => {
    const stage = stageRef.current;
    if (!stage) return 0;
    return stage.clientWidth * zoomRef.current;
  };

  const timeFromClientX = (clientX: number) => {
    const stage = stageRef.current;
    if (!stage || !timelineDuration) return 0;
    const width = innerWidth();
    if (!width) return 0;
    const x =
      clientX - stage.getBoundingClientRect().left + stage.scrollLeft;
    const ratio = Math.min(1, Math.max(0, x / width));
    return ratio * timelineDuration;
  };

  const applyZoom = (nextZoom: number, clientX?: number) => {
    const stage = stageRef.current;
    const next = Number(clamp(nextZoom, MIN_ZOOM, MAX_ZOOM).toFixed(2));
    if (Math.abs(next - zoomRef.current) < 0.01) return;
    const rect = stage?.getBoundingClientRect();
    const width = innerWidth();
    const viewOffset =
      clientX != null && rect
        ? clientX - rect.left
        : (rect?.width || 0) / 2;
    const cursorX =
      (rect ? viewOffset : 0) + (stage?.scrollLeft || 0);
    pendingScrollRef.current = {
      ratio: width > 0 ? cursorX / width : 0,
      viewOffset,
    };
    setZoom(next);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return undefined;

    setReady(false);
    setLoadError(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
    setZoom(1);

    const onLoaded = () => {
      const next = audio.duration;
      if (Number.isFinite(next) && next > 0) setAudioDuration(next);
      setReady(true);
      setLoadError(false);
    };
    const onTime = () => {
      setCurrentTime(audio.currentTime || 0);
      onTimeChangeRef.current?.(audio.currentTime || 0);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onError = () => {
      setLoadError(true);
      setReady(false);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("canplay", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onPause);
    audio.addEventListener("error", onError);
    audio.load();

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("canplay", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onPause);
      audio.removeEventListener("error", onError);
      audio.pause();
    };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || seekRequest?.time == null || !timelineDuration) return;
    const next = Math.min(Math.max(seekRequest.time, 0), timelineDuration);
    if (audio.readyState >= 1) {
      audio.currentTime = next;
      setCurrentTime(next);
    }
  }, [seekRequest, timelineDuration]);

  useEffect(() => {
    const stage = stageRef.current;
    const pending = pendingScrollRef.current;
    if (!stage || !pending) return;
    const width = stage.clientWidth * zoom;
    stage.scrollLeft = pending.ratio * width - pending.viewOffset;
    pendingScrollRef.current = null;
  }, [zoom]);

  const scrollTimeIntoView = (time: number) => {
    const stage = stageRef.current;
    if (!stage || zoomRef.current <= 1 || !timelineDuration) return;
    const width = stage.clientWidth * zoomRef.current;
    const x = (time / timelineDuration) * width;
    const view = stage.clientWidth;
    const left = stage.scrollLeft;
    const pad = view * 0.18;
    if (x < left + pad || x > left + view - pad) {
      stage.scrollLeft = Math.max(0, x - view * 0.4);
    }
  };

  useEffect(() => {
    if (!isPlaying) return;
    scrollTimeIntoView(currentTime);
  }, [currentTime, isPlaying, timelineDuration, zoom]);

  useEffect(() => {
    if (seekRequest?.time == null) return;
    scrollTimeIntoView(seekRequest.time);
  }, [seekRequest, timelineDuration, zoom]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const factor = event.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
        applyZoom(zoomRef.current * factor, event.clientX);
        return;
      }
      if (zoomRef.current > 1 && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        event.preventDefault();
        stage.scrollLeft += event.deltaY;
      }
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, []);

  const timedEvents = useMemo(() => {
    const total = timelineDuration;
    return events
      .map((event, index) => {
        const audioTime = connectedAudioTime(event);
        if (audioTime == null || audioTime < -0.05) return null;
        if (total > 0 && audioTime > total + 0.75) return null;
        return {
          ...event,
          sourceIndex: index,
          audioTime: Math.max(0, audioTime),
        };
      })
      .filter((event): event is TimedEvent => event != null);
  }, [events, timelineDuration]);

  const speechRanges = useMemo(
    () => pairSpeechRanges(timedEvents),
    [timedEvents]
  );

  useEffect(() => {
    let cancelled = false;
    audioBufferRef.current = null;
    setWaveform(null);
    if (!audioUrl && !callUuid) return undefined;
    const loadWave = async () => {
      try {
        const bytes = await fetchRecordingBytes(audioUrl, callUuid);
        if (cancelled || !bytes) return;
        const buffer = await decodeAudioBuffer(bytes);
        if (cancelled) return;
        audioBufferRef.current = buffer;
        setWaveform(buildWaveform(buffer, speechRanges));
      } catch {
        if (!cancelled) setWaveform(null);
      }
    };
    loadWave();
    return () => {
      cancelled = true;
    };
    // speechRanges is applied in the next effect so we don't refetch on filter changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, callUuid]);

  useEffect(() => {
    if (!audioBufferRef.current) return;
    setWaveform(buildWaveform(audioBufferRef.current, speechRanges));
  }, [speechRanges]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const inner = innerRef.current;
    if (!canvas || !inner || !waveform) return undefined;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = inner.clientWidth;
      const height = waveform.dual ? 132 : 88;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const progress =
        timelineDuration > 0 ? currentTime / timelineDuration : 0;
      drawWaveform(canvas, waveform, progress);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(inner);
    return () => observer.disconnect();
    // currentTime is painted in the effect below so resize doesn't wipe the canvas every tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveform, zoom, timelineDuration]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !waveform) return;
    const progress =
      timelineDuration > 0 ? currentTime / timelineDuration : 0;
    drawWaveform(canvas, waveform, progress);
  }, [currentTime, waveform, timelineDuration]);

  const markerPct = (time: number) =>
    timelineDuration > 0 ? `${(time / timelineDuration) * 100}%` : "0%";

  const nearestEvent = (time: number): TimedEvent | null => {
    if (!timedEvents.length) return null;
    const visible = timelineDuration / Math.max(zoom, 1);
    const threshold = Math.max(0.12, visible * 0.012);
    let best: TimedEvent | null = null;
    let bestDiff = threshold;
    timedEvents.forEach((event) => {
      const diff = Math.abs(event.audioTime - time);
      if (diff <= bestDiff) {
        best = event;
        bestDiff = diff;
      }
    });
    return best;
  };

  const seekTo = (time: number) => {
    const audio = audioRef.current;
    const next = Math.min(Math.max(time, 0), timelineDuration || time);
    setCurrentTime(next);
    if (audio && audio.readyState >= 1) {
      audio.currentTime = next;
    }
  };

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setLoadError(true);
      }
    } else {
      audio.pause();
    }
  };

  const hoveredEvent = hover?.event || null;
  const hoverFlipLeft = (() => {
    if (!hover) return false;
    const stage = stageRef.current;
    if (!stage) return false;
    const rect = stage.getBoundingClientRect();
    return hover.x > rect.left + rect.width * 0.62;
  })();
  const zoomLabel = zoom === 1 ? "Fit" : `${zoom % 1 === 0 ? zoom : zoom.toFixed(1)}×`;

  return (
    <Box className="events-player">
      <audio ref={audioRef} src={audioUrl || undefined} preload="metadata" />
      <Box className="events-player-toolbar">
        <IconButton
          className="events-player-play"
          onClick={handlePlayPause}
          disabled={!audioUrl}
          size="small"
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>
        <Typography className="events-player-clock">
          {formatClock(currentTime)} / {formatClock(timelineDuration)}
        </Typography>
        <Typography className="events-player-clock-label">
          from connected
        </Typography>
        <Typography className="events-player-hint">
          {loadError
            ? "Recording could not be loaded — showing event times only"
            : !ready
            ? "Loading recording…"
            : "Click to jump · Ctrl+scroll to zoom · scroll to pan"}
        </Typography>
        <Box className="events-player-zoom">
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                className="events-player-zoom-btn"
                size="small"
                disabled={zoom <= MIN_ZOOM}
                onClick={() => applyZoom(zoom / ZOOM_STEP)}
              >
                <Remove fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <button
              type="button"
              className="events-player-zoom-label"
              onClick={() => applyZoom(1)}
            >
              {zoomLabel}
            </button>
          </Tooltip>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                className="events-player-zoom-btn"
                size="small"
                disabled={zoom >= MAX_ZOOM}
                onClick={() => applyZoom(zoom * ZOOM_STEP)}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box className="events-legend" aria-label="Color legend">
        <span className="events-legend-item">
          <i style={{ background: WAVEFORM_LANE_COLORS.caller }} />
          Caller
        </span>
        <span className="events-legend-item">
          <i style={{ background: WAVEFORM_LANE_COLORS.bot }} />
          Bot
        </span>
        <span className="events-legend-sep" />
        {EVENT_LEGEND_KEYS.map((key) => (
          <span className="events-legend-item" key={key}>
            <i style={{ background: CATEGORY_META[key].color }} />
            {CATEGORY_META[key].label}
          </span>
        ))}
      </Box>

      <Box className={`events-player-wave${waveform?.dual ? " is-dual" : ""}`}>
        {waveform && waveform.lanes.length > 0 && (
          <Box className="events-player-lane-labels">
            {waveform.lanes.map((lane) => (
              <span key={lane.label} style={{ color: lane.color }}>
                {lane.label}
              </span>
            ))}
          </Box>
        )}
      <Box ref={stageRef} className="events-player-stage">
        <Box
          ref={innerRef}
          className="events-player-inner"
          style={{ width: `${zoom * 100}%` }}
          onMouseMove={(event) => {
            const time = timeFromClientX(event.clientX);
            setHover({
              time,
              x: event.clientX,
              event: nearestEvent(time),
            });
          }}
          onMouseLeave={() => setHover(null)}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest(".events-player-line")) {
              return;
            }
            const time = timeFromClientX(event.clientX);
            seekTo(time);
          }}
        >
          <Box
            className={`events-player-track${waveform ? " has-wave" : ""}${
              waveform?.dual ? " is-dual" : ""
            }`}
          >
            <canvas ref={canvasRef} className="events-player-wave-canvas" />
            {timelineDuration > 0 && !waveform && (
              <Box
                className="events-player-progress"
                style={{ width: markerPct(currentTime) }}
              />
            )}
            {!waveform &&
              speechRanges.map((range, index) => (
                <Box
                  key={`range-${index}`}
                  className="events-player-range"
                  title={range.preview || "Bot speaking"}
                  style={{
                    left: markerPct(range.start),
                    width: markerPct(range.end - range.start),
                    background: hexToRgba(CATEGORY_META.speech.color, 0.28),
                  }}
                />
              ))}
            {timedEvents.map((event, index) => {
              const meta =
                CATEGORY_META[event.category as CategoryKey] ||
                CATEGORY_META.other;
              const isActive = event.sourceIndex === activeIndex;
              return (
                <button
                  key={`${event.event_name}-${index}`}
                  type="button"
                  className={`events-player-line${isActive ? " active" : ""}`}
                  style={{
                    left: markerPct(event.audioTime),
                    background: meta.color,
                  }}
                  title={event.label}
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    seekTo(event.audioTime);
                    onMarkerClick?.(event.sourceIndex, event.audioTime);
                  }}
                  aria-label={event.label}
                />
              );
            })}
            {timelineDuration > 0 && (
              <Box
                className="events-player-playhead"
                style={{ left: markerPct(currentTime) }}
              />
            )}
          </Box>
          {hover && timelineDuration > 0 && (
            <Box
              className={`events-player-hover${hoverFlipLeft ? " is-left" : ""}`}
              style={{
                left: markerPct(hover.time),
              }}
            >
              <Box className="events-player-hover-card">
                <div className="events-player-hover-time">
                  {formatClockMs(hover.time)}
                </div>
                {hoveredEvent && (
                  <>
                    <div className="events-player-hover-label">
                      {hoveredEvent.label}
                    </div>
                    <div className="events-player-hover-meta">
                      Start {formatClockMs(hoveredEvent.from_start)} · Conn{" "}
                      {formatClockMs(hoveredEvent.from_connected)}
                    </div>
                    {hoveredEvent.preview ? (
                      <div className="events-player-hover-preview">
                        {hoveredEvent.preview}
                      </div>
                    ) : null}
                  </>
                )}
              </Box>
              <Box className="events-player-hover-line" />
            </Box>
          )}
          <Box className="events-player-axis">
            {axisTicks(timelineDuration, zoom).map((tick) => (
              <span
                key={tick}
                className="events-player-tick"
                style={{ left: markerPct(tick) }}
              >
                {formatClock(tick)}
              </span>
            ))}
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default CallEventsPlayer;
